import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    createWarehouse,
    getWarehouses,
    addWarehouseOperation,
    getWarehouseOperations,
} from "../../api/warehouseService";

import {
    createWarehouseZone,
    getWarehouseZonesByWarehouse,
    deactivateWarehouseZone,
} from "../../api/warehouseZoneService";

import {
    createWarehouseShelf,
    getWarehouseShelvesByZone,
    setWarehouseShelfBlocked,
    deleteWarehouseShelf,
} from "../../api/warehouseShelfService";

import {
    storeWarehouseInventory,
    getInventoryByWarehouse,
    releaseWarehouseInventory,
    moveWarehouseInventory,
    recalculateWarehouseShelf,
} from "../../api/warehouseInventoryService";

import {
    PageHeader,
    Panel,
    KpiCard,
} from "../../components/ui/AdminUI";

import DataTable from "../../components/ui/DataTable";
import StatusBadge from "../../components/ui/StatusBadge";

import {
    TextInput,
    NumberInput,
    SelectBox,
    PrimaryButton,
    SuccessButton,
    SecondaryButton,
} from "../../components/ui/FormControls";

const TABS = [
    { key: "warehouses", label: "Warehouses" },
    { key: "zones", label: "Zones" },
    { key: "shelves", label: "Shelves" },
    { key: "inventory", label: "Inventory" },
    { key: "operations", label: "Operations" },
];

const EMPTY_WAREHOUSE = {
    name: "",
    addressId: "",
    managerId: "",
    capacity: "",
};

const EMPTY_ZONE = {
    name: "",
    zoneType: "Storage",
    description: "",
    capacity: "",
    requiresTemperatureControl: false,
    minTemperatureCelsius: "",
    maxTemperatureCelsius: "",
};

const EMPTY_SHELF = {
    code: "",
    maxWeightKg: "",
    maxVolumeM3: "",
    isBlocked: false,
    notes: "",
};

const EMPTY_INVENTORY = {
    warehouseShelfId: "",
    shipmentId: "",
    parcelId: "",
    weightKg: "",
    volumeM3: "",
    notes: "",
};

const EMPTY_OPERATION = {
    shipmentId: "",
    parcelId: "",
    operationType: "Received",
    notes: "",
};

const EMPTY_MOVE = {
    inventoryId: "",
    targetShelfId: "",
    notes: "",
};

export default function WarehouseManagementPage() {
    const [activeTab, setActiveTab] = useState("warehouses");

    const [warehouses, setWarehouses] = useState([]);
    const [zones, setZones] = useState([]);
    const [shelves, setShelves] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [operations, setOperations] = useState([]);

    const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
    const [selectedZoneId, setSelectedZoneId] = useState("");

    const [warehouseForm, setWarehouseForm] = useState(EMPTY_WAREHOUSE);
    const [zoneForm, setZoneForm] = useState(EMPTY_ZONE);
    const [shelfForm, setShelfForm] = useState(EMPTY_SHELF);
    const [inventoryForm, setInventoryForm] = useState(EMPTY_INVENTORY);
    const [operationForm, setOperationForm] = useState(EMPTY_OPERATION);
    const [moveForm, setMoveForm] = useState(EMPTY_MOVE);

    const [includeReleased, setIncludeReleased] = useState(false);

    const [loading, setLoading] = useState({
        warehouses: true,
        zones: false,
        shelves: false,
        inventory: false,
        operations: false,
    });

    const [saving, setSaving] = useState({
        warehouse: false,
        zone: false,
        shelf: false,
        inventory: false,
        operation: false,
        move: false,
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const showError = (err, fallback) => {
        console.error(err);

        const validationErrors = err?.response?.data?.errors;

        if (validationErrors) {
            const messages = Object.values(validationErrors)
                .flat()
                .filter(Boolean);

            if (messages.length > 0) {
                setError(messages.join(" "));
                return;
            }
        }

        setError(
            err?.response?.data?.message ||
            err?.message ||
            fallback
        );
    };

    const clearMessages = () => {
        setError("");
        setSuccess("");
    };

    const selectedWarehouse = useMemo(
        () =>
            warehouses.find(
                (item) =>
                    String(item.id) === String(selectedWarehouseId)
            ) || null,
        [warehouses, selectedWarehouseId]
    );

    const selectedZone = useMemo(
        () =>
            zones.find(
                (item) =>
                    String(item.id) === String(selectedZoneId)
            ) || null,
        [zones, selectedZoneId]
    );

    const loadWarehouses = useCallback(async () => {
        try {
            setLoading((prev) => ({ ...prev, warehouses: true }));
            clearMessages();

            const data = await getWarehouses();
            const list = Array.isArray(data) ? data : [];

            setWarehouses(list);

            setSelectedWarehouseId((current) => {
                if (
                    current &&
                    list.some((item) => String(item.id) === String(current))
                ) {
                    return current;
                }

                return list[0]?.id ? String(list[0].id) : "";
            });
        } catch (err) {
            showError(err, "Failed to load warehouses.");
        } finally {
            setLoading((prev) => ({ ...prev, warehouses: false }));
        }
    }, []);

    const loadZones = useCallback(async () => {
        if (!selectedWarehouseId) {
            setZones([]);
            setSelectedZoneId("");
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, zones: true }));
            clearMessages();

            const data = await getWarehouseZonesByWarehouse(
                Number(selectedWarehouseId)
            );

            const list = Array.isArray(data) ? data : [];
            setZones(list);

            setSelectedZoneId((current) => {
                if (
                    current &&
                    list.some((item) => String(item.id) === String(current))
                ) {
                    return current;
                }

                return list[0]?.id ? String(list[0].id) : "";
            });
        } catch (err) {
            showError(err, "Failed to load warehouse zones.");
        } finally {
            setLoading((prev) => ({ ...prev, zones: false }));
        }
    }, [selectedWarehouseId]);

    const loadShelves = useCallback(async () => {
        if (!selectedZoneId) {
            setShelves([]);
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, shelves: true }));
            clearMessages();

            const data = await getWarehouseShelvesByZone(
                Number(selectedZoneId)
            );

            setShelves(Array.isArray(data) ? data : []);
        } catch (err) {
            showError(err, "Failed to load warehouse shelves.");
        } finally {
            setLoading((prev) => ({ ...prev, shelves: false }));
        }
    }, [selectedZoneId]);

    const loadInventory = useCallback(async () => {
        if (!selectedWarehouseId) {
            setInventory([]);
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, inventory: true }));
            clearMessages();

            const data = await getInventoryByWarehouse(
                Number(selectedWarehouseId),
                includeReleased
            );

            setInventory(Array.isArray(data) ? data : []);
        } catch (err) {
            showError(err, "Failed to load warehouse inventory.");
        } finally {
            setLoading((prev) => ({ ...prev, inventory: false }));
        }
    }, [selectedWarehouseId, includeReleased]);

    const loadOperations = useCallback(async () => {
        if (!selectedWarehouseId) {
            setOperations([]);
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, operations: true }));
            clearMessages();

            const data = await getWarehouseOperations(
                Number(selectedWarehouseId)
            );

            setOperations(Array.isArray(data) ? data : []);
        } catch (err) {
            showError(err, "Failed to load warehouse operations.");
        } finally {
            setLoading((prev) => ({ ...prev, operations: false }));
        }
    }, [selectedWarehouseId]);

    useEffect(() => {
        loadWarehouses();
    }, [loadWarehouses]);

    useEffect(() => {
        loadZones();
        loadInventory();
        loadOperations();
    }, [loadZones, loadInventory, loadOperations]);

    useEffect(() => {
        loadShelves();
    }, [loadShelves]);

    const refreshCurrentTab = async () => {
        switch (activeTab) {
            case "warehouses":
                await loadWarehouses();
                break;
            case "zones":
                await loadZones();
                break;
            case "shelves":
                await loadShelves();
                break;
            case "inventory":
                await loadInventory();
                break;
            case "operations":
                await loadOperations();
                break;
            default:
                break;
        }
    };

    const handleCreateWarehouse = async (event) => {
        event.preventDefault();
        clearMessages();

        const name = warehouseForm.name.trim();
        const capacity = Number(warehouseForm.capacity);

        if (!name) {
            setError("Warehouse name is required.");
            return;
        }

        if (!Number.isInteger(capacity) || capacity <= 0) {
            setError("Warehouse capacity must be a positive integer.");
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, warehouse: true }));

            const created = await createWarehouse({
                name,
                addressId: warehouseForm.addressId
                    ? Number(warehouseForm.addressId)
                    : null,
                managerId: warehouseForm.managerId
                    ? Number(warehouseForm.managerId)
                    : null,
                capacity,
            });

            setWarehouseForm(EMPTY_WAREHOUSE);
            setSuccess("Warehouse created successfully.");

            await loadWarehouses();

            if (created?.id) {
                setSelectedWarehouseId(String(created.id));
            }
        } catch (err) {
            showError(err, "Failed to create warehouse.");
        } finally {
            setSaving((prev) => ({ ...prev, warehouse: false }));
        }
    };

    const handleCreateZone = async (event) => {
        event.preventDefault();
        clearMessages();

        if (!selectedWarehouseId) {
            setError("Select a warehouse first.");
            return;
        }

        const capacity = Number(zoneForm.capacity);

        if (!zoneForm.name.trim()) {
            setError("Zone name is required.");
            return;
        }

        if (!Number.isFinite(capacity) || capacity < 0) {
            setError("Zone capacity cannot be negative.");
            return;
        }

        if (
            zoneForm.requiresTemperatureControl &&
            (
                zoneForm.minTemperatureCelsius === "" ||
                zoneForm.maxTemperatureCelsius === ""
            )
        ) {
            setError(
                "Minimum and maximum temperature are required."
            );
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, zone: true }));

            const created = await createWarehouseZone({
                warehouseId: Number(selectedWarehouseId),
                ...zoneForm,
                capacity,
            });

            setZoneForm(EMPTY_ZONE);
            setSuccess("Warehouse zone created successfully.");

            await loadZones();

            if (created?.id) {
                setSelectedZoneId(String(created.id));
            }
        } catch (err) {
            showError(err, "Failed to create warehouse zone.");
        } finally {
            setSaving((prev) => ({ ...prev, zone: false }));
        }
    };

    const handleCreateShelf = async (event) => {
        event.preventDefault();
        clearMessages();

        if (!selectedZoneId) {
            setError("Select a warehouse zone first.");
            return;
        }

        const maxWeightKg = Number(shelfForm.maxWeightKg);
        const maxVolumeM3 = Number(shelfForm.maxVolumeM3);

        if (!shelfForm.code.trim()) {
            setError("Shelf code is required.");
            return;
        }

        if (!Number.isFinite(maxWeightKg) || maxWeightKg < 0) {
            setError("Maximum weight cannot be negative.");
            return;
        }

        if (!Number.isFinite(maxVolumeM3) || maxVolumeM3 < 0) {
            setError("Maximum volume cannot be negative.");
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, shelf: true }));

            await createWarehouseShelf({
                warehouseZoneId: Number(selectedZoneId),
                ...shelfForm,
                maxWeightKg,
                maxVolumeM3,
            });

            setShelfForm(EMPTY_SHELF);
            setSuccess("Warehouse shelf created successfully.");
            await loadShelves();
        } catch (err) {
            showError(err, "Failed to create warehouse shelf.");
        } finally {
            setSaving((prev) => ({ ...prev, shelf: false }));
        }
    };

    const handleStoreInventory = async (event) => {
        event.preventDefault();
        clearMessages();

        if (!inventoryForm.warehouseShelfId) {
            setError("Select a warehouse shelf.");
            return;
        }

        if (!inventoryForm.shipmentId && !inventoryForm.parcelId) {
            setError("Shipment ID or Parcel ID is required.");
            return;
        }

        const weightKg = Number(inventoryForm.weightKg);
        const volumeM3 = Number(inventoryForm.volumeM3);

        if (!Number.isFinite(weightKg) || weightKg <= 0) {
            setError("Weight must be greater than zero.");
            return;
        }

        if (!Number.isFinite(volumeM3) || volumeM3 <= 0) {
            setError("Volume must be greater than zero.");
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, inventory: true }));

            await storeWarehouseInventory({
                ...inventoryForm,
                weightKg,
                volumeM3,
            });

            setInventoryForm(EMPTY_INVENTORY);
            setSuccess("Inventory stored successfully.");

            await Promise.all([
                loadInventory(),
                loadShelves(),
                loadZones(),
            ]);
        } catch (err) {
            showError(err, "Failed to store inventory.");
        } finally {
            setSaving((prev) => ({ ...prev, inventory: false }));
        }
    };

    const handleAddOperation = async (event) => {
        event.preventDefault();
        clearMessages();

        if (!selectedWarehouseId) {
            setError("Select a warehouse first.");
            return;
        }

        if (!operationForm.shipmentId && !operationForm.parcelId) {
            setError("Shipment ID or Parcel ID is required.");
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, operation: true }));

            await addWarehouseOperation(
                Number(selectedWarehouseId),
                {
                    shipmentId: operationForm.shipmentId
                        ? Number(operationForm.shipmentId)
                        : null,
                    parcelId: operationForm.parcelId
                        ? Number(operationForm.parcelId)
                        : null,
                    operationType: operationForm.operationType,
                    notes: operationForm.notes.trim() || null,
                }
            );

            setOperationForm(EMPTY_OPERATION);
            setSuccess("Warehouse operation added successfully.");
            await loadOperations();
        } catch (err) {
            showError(err, "Failed to add warehouse operation.");
        } finally {
            setSaving((prev) => ({ ...prev, operation: false }));
        }
    };

    const handleMoveInventory = async (event) => {
        event.preventDefault();
        clearMessages();

        if (!moveForm.inventoryId || !moveForm.targetShelfId) {
            setError("Inventory item and target shelf are required.");
            return;
        }

        try {
            setSaving((prev) => ({ ...prev, move: true }));

            await moveWarehouseInventory(
                Number(moveForm.inventoryId),
                Number(moveForm.targetShelfId),
                moveForm.notes
            );

            setMoveForm(EMPTY_MOVE);
            setSuccess("Inventory moved successfully.");

            await Promise.all([
                loadInventory(),
                loadShelves(),
                loadZones(),
            ]);
        } catch (err) {
            showError(err, "Failed to move inventory.");
        } finally {
            setSaving((prev) => ({ ...prev, move: false }));
        }
    };

    const handleReleaseInventory = async (item) => {
        clearMessages();

        try {
            await releaseWarehouseInventory(
                item.id,
                "Released from warehouse interface."
            );

            setSuccess("Inventory released successfully.");

            await Promise.all([
                loadInventory(),
                loadShelves(),
                loadZones(),
            ]);
        } catch (err) {
            showError(err, "Failed to release inventory.");
        }
    };

    const handleToggleShelfBlocked = async (shelf) => {
        clearMessages();

        try {
            await setWarehouseShelfBlocked(
                shelf.id,
                !shelf.isBlocked
            );

            setSuccess(
                shelf.isBlocked
                    ? "Shelf unblocked successfully."
                    : "Shelf blocked successfully."
            );

            await loadShelves();
        } catch (err) {
            showError(err, "Failed to update shelf state.");
        }
    };

    const handleDeleteShelf = async (shelf) => {
        clearMessages();

        try {
            await deleteWarehouseShelf(shelf.id);
            setSuccess("Warehouse shelf deleted successfully.");
            await loadShelves();
        } catch (err) {
            showError(err, "Failed to delete warehouse shelf.");
        }
    };

    const handleDeactivateZone = async (zone) => {
        clearMessages();

        try {
            await deactivateWarehouseZone(zone.id);
            setSuccess("Warehouse zone deactivated successfully.");
            await loadZones();
        } catch (err) {
            showError(err, "Failed to deactivate warehouse zone.");
        }
    };

    const handleRecalculateShelf = async (shelfId) => {
        clearMessages();

        try {
            await recalculateWarehouseShelf(Number(shelfId));
            setSuccess("Shelf values recalculated successfully.");

            await Promise.all([
                loadShelves(),
                loadInventory(),
                loadZones(),
            ]);
        } catch (err) {
            showError(err, "Failed to recalculate warehouse shelf.");
        }
    };

    const stats = useMemo(() => {
        const activeWarehouses = warehouses.filter(
            (item) => item.isActive === true
        ).length;

        const activeZones = zones.filter(
            (item) => item.isActive === true
        ).length;

        const occupiedShelves = shelves.filter(
            (item) => item.isOccupied === true
        ).length;

        const activeInventory = inventory.filter(
            (item) => !item.releasedAt
        ).length;

        return {
            warehouses: warehouses.length,
            activeWarehouses,
            zones: zones.length,
            activeZones,
            shelves: shelves.length,
            occupiedShelves,
            inventory: activeInventory,
            operations: operations.length,
        };
    }, [warehouses, zones, shelves, inventory, operations]);

    const warehouseColumns = [
        { key: "id", label: "ID" },
        {
            key: "name",
            label: "Warehouse",
            render: (row) => (
                <span className="font-bold text-slate-900">
                    {row.name || "�"}
                </span>
            ),
        },
        {
            key: "addressId",
            label: "Address",
            render: (row) =>
                row.address?.fullAddress ||
                row.address?.addressLine ||
                row.addressId ||
                "�",
        },
        {
            key: "managerId",
            label: "Manager",
            render: (row) =>
                row.manager?.userName ||
                row.manager?.email ||
                row.managerId ||
                "�",
        },
        {
            key: "capacity",
            label: "Capacity",
            render: (row) =>
                formatNumber(row.capacity),
        },
        {
            key: "isActive",
            label: "Status",
            render: (row) => (
                <StatusBadge
                    status={row.isActive ? "Active" : "Inactive"}
                />
            ),
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => (
                <PrimaryButton
                    type="button"
                    className="px-3 py-2"
                    disabled={
                        String(row.id) ===
                        String(selectedWarehouseId)
                    }
                    onClick={() => {
                        setSelectedWarehouseId(String(row.id));
                        setActiveTab("zones");
                    }}
                >
                    {String(row.id) === String(selectedWarehouseId)
                        ? "Selected"
                        : "Select"}
                </PrimaryButton>
            ),
        },
    ];

    const zoneColumns = [
        { key: "id", label: "ID" },
        { key: "name", label: "Zone" },
        {
            key: "zoneType",
            label: "Type",
            render: (row) => (
                <StatusBadge status={row.zoneType || "Unknown"} />
            ),
        },
        {
            key: "capacity",
            label: "Capacity",
            render: (row) => formatNumber(row.capacity),
        },
        {
            key: "currentLoad",
            label: "Current Load",
            render: (row) => formatNumber(row.currentLoad),
        },
        {
            key: "temperature",
            label: "Temperature",
            render: (row) =>
                row.requiresTemperatureControl
                    ? `${row.minTemperatureCelsius ?? "�"} �C to ${row.maxTemperatureCelsius ?? "�"
                    } �C`
                    : "Not controlled",
        },
        {
            key: "isActive",
            label: "Status",
            render: (row) => (
                <StatusBadge
                    status={row.isActive ? "Active" : "Inactive"}
                />
            ),
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => (
                <div className="flex flex-wrap gap-2">
                    <PrimaryButton
                        type="button"
                        className="px-3 py-2"
                        onClick={() => {
                            setSelectedZoneId(String(row.id));
                            setActiveTab("shelves");
                        }}
                    >
                        Select
                    </PrimaryButton>

                    {row.isActive && (
                        <SecondaryButton
                            type="button"
                            className="px-3 py-2"
                            onClick={() => handleDeactivateZone(row)}
                        >
                            Deactivate
                        </SecondaryButton>
                    )}
                </div>
            ),
        },
    ];

    const shelfColumns = [
        { key: "id", label: "ID" },
        { key: "code", label: "Shelf" },
        {
            key: "weight",
            label: "Weight",
            render: (row) =>
                `${formatNumber(row.currentWeightKg)} / ${formatNumber(
                    row.maxWeightKg
                )} kg`,
        },
        {
            key: "volume",
            label: "Volume",
            render: (row) =>
                `${formatNumber(row.currentVolumeM3)} / ${formatNumber(
                    row.maxVolumeM3
                )} m?`,
        },
        {
            key: "occupied",
            label: "Occupied",
            render: (row) => (
                <StatusBadge
                    status={row.isOccupied ? "Occupied" : "Free"}
                />
            ),
        },
        {
            key: "blocked",
            label: "Blocked",
            render: (row) => (
                <StatusBadge
                    status={row.isBlocked ? "Blocked" : "Available"}
                />
            ),
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => (
                <div className="flex flex-wrap gap-2">
                    <SecondaryButton
                        type="button"
                        className="px-3 py-2"
                        onClick={() =>
                            handleToggleShelfBlocked(row)
                        }
                    >
                        {row.isBlocked ? "Unblock" : "Block"}
                    </SecondaryButton>

                    <SecondaryButton
                        type="button"
                        className="px-3 py-2"
                        onClick={() =>
                            handleRecalculateShelf(row.id)
                        }
                    >
                        Recalculate
                    </SecondaryButton>

                    <SecondaryButton
                        type="button"
                        className="px-3 py-2"
                        onClick={() => handleDeleteShelf(row)}
                    >
                        Delete
                    </SecondaryButton>
                </div>
            ),
        },
    ];

    const inventoryColumns = [
        { key: "id", label: "ID" },
        {
            key: "shelf",
            label: "Shelf",
            render: (row) =>
                row.warehouseShelf?.code ||
                row.warehouseShelfId ||
                "�",
        },
        {
            key: "shipmentId",
            label: "Shipment",
            render: (row) => row.shipmentId ?? "�",
        },
        {
            key: "parcelId",
            label: "Parcel",
            render: (row) => row.parcelId ?? "�",
        },
        {
            key: "weightKg",
            label: "Weight",
            render: (row) => `${formatNumber(row.weightKg)} kg`,
        },
        {
            key: "volumeM3",
            label: "Volume",
            render: (row) => `${formatNumber(row.volumeM3)} m?`,
        },
        {
            key: "status",
            label: "Status",
            render: (row) => (
                <StatusBadge status={row.status || "Unknown"} />
            ),
        },
        {
            key: "storedAt",
            label: "Stored",
            render: (row) => formatDateTime(row.storedAt),
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) =>
                row.releasedAt ? (
                    <span className="text-sm text-slate-500">
                        Released {formatDateTime(row.releasedAt)}
                    </span>
                ) : (
                    <SecondaryButton
                        type="button"
                        className="px-3 py-2"
                        onClick={() =>
                            handleReleaseInventory(row)
                        }
                    >
                        Release
                    </SecondaryButton>
                ),
        },
    ];

    const operationColumns = [
        { key: "id", label: "ID" },
        {
            key: "shipmentId",
            label: "Shipment",
            render: (row) => row.shipmentId ?? "�",
        },
        {
            key: "parcelId",
            label: "Parcel",
            render: (row) => row.parcelId ?? "�",
        },
        {
            key: "operationType",
            label: "Operation",
            render: (row) => (
                <StatusBadge
                    status={row.operationType || "Unknown"}
                />
            ),
        },
        {
            key: "processedBy",
            label: "Processed By",
            render: (row) =>
                row.processedByUser?.userName ||
                row.processedByUser?.email ||
                row.processedByUserId ||
                "System",
        },
        {
            key: "notes",
            label: "Notes",
            render: (row) => row.notes || "�",
        },
        {
            key: "operationDate",
            label: "Date",
            render: (row) =>
                formatDateTime(row.operationDate),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Warehouse Management"
                subtitle="Manage warehouses, zones, shelves, inventory and warehouse operations."
                action={
                    <SecondaryButton
                        type="button"
                        onClick={refreshCurrentTab}
                    >
                        Refresh Current Tab
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    title="Warehouses"
                    value={`${stats.activeWarehouses}/${stats.warehouses}`}
                    color="blue"
                />

                <KpiCard
                    title="Zones"
                    value={`${stats.activeZones}/${stats.zones}`}
                    color="green"
                />

                <KpiCard
                    title="Occupied Shelves"
                    value={`${stats.occupiedShelves}/${stats.shelves}`}
                    color="purple"
                />

                <KpiCard
                    title="Active Inventory"
                    value={stats.inventory}
                    color="teal"
                />
            </div>

            <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`rounded-xl px-4 py-2 font-semibold transition ${activeTab === tab.key
                                ? "bg-slate-900 text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "warehouses" && (
                <div className="space-y-6">
                    <Panel
                        title="Create Warehouse"
                        subtitle="Register a new warehouse facility."
                    >
                        <form
                            onSubmit={handleCreateWarehouse}
                            className="grid gap-4 md:grid-cols-2"
                        >
                            <TextInput
                                label="Warehouse Name"
                                value={warehouseForm.name}
                                onChange={(event) =>
                                    setWarehouseForm((prev) => ({
                                        ...prev,
                                        name: event.target.value,
                                    }))
                                }
                                maxLength={150}
                                required
                            />

                            <NumberInput
                                label="Capacity"
                                value={warehouseForm.capacity}
                                onChange={(event) =>
                                    setWarehouseForm((prev) => ({
                                        ...prev,
                                        capacity: event.target.value,
                                    }))
                                }
                                min="1"
                                step="1"
                                required
                            />

                            <NumberInput
                                label="Address ID"
                                value={warehouseForm.addressId}
                                onChange={(event) =>
                                    setWarehouseForm((prev) => ({
                                        ...prev,
                                        addressId: event.target.value,
                                    }))
                                }
                                min="1"
                                step="1"
                            />

                            <NumberInput
                                label="Manager User ID"
                                value={warehouseForm.managerId}
                                onChange={(event) =>
                                    setWarehouseForm((prev) => ({
                                        ...prev,
                                        managerId: event.target.value,
                                    }))
                                }
                                min="1"
                                step="1"
                            />

                            <div className="md:col-span-2">
                                <SuccessButton
                                    type="submit"
                                    disabled={saving.warehouse}
                                >
                                    {saving.warehouse
                                        ? "Creating..."
                                        : "Create Warehouse"}
                                </SuccessButton>
                            </div>
                        </form>
                    </Panel>

                    <Panel
                        title="Warehouses"
                        subtitle="Registered warehouse facilities."
                    >
                        {loading.warehouses ? (
                            <p className="text-slate-500">
                                Loading warehouses...
                            </p>
                        ) : (
                            <DataTable
                                columns={warehouseColumns}
                                rows={warehouses}
                                rowKey="id"
                                emptyText="No warehouses found."
                            />
                        )}
                    </Panel>
                </div>
            )}

            {activeTab === "zones" && (
                <div className="space-y-6">
                    <WarehouseSelector
                        warehouses={warehouses}
                        selectedWarehouseId={selectedWarehouseId}
                        setSelectedWarehouseId={setSelectedWarehouseId}
                    />

                    <Panel
                        title="Create Warehouse Zone"
                        subtitle="Create storage, cold, hazardous or processing zones."
                    >
                        <form
                            onSubmit={handleCreateZone}
                            className="grid gap-4 md:grid-cols-2"
                        >
                            <TextInput
                                label="Zone Name"
                                value={zoneForm.name}
                                onChange={(event) =>
                                    setZoneForm((prev) => ({
                                        ...prev,
                                        name: event.target.value,
                                    }))
                                }
                                required
                            />

                            <SelectBox
                                label="Zone Type"
                                value={zoneForm.zoneType}
                                onChange={(event) =>
                                    setZoneForm((prev) => ({
                                        ...prev,
                                        zoneType: event.target.value,
                                    }))
                                }
                            >
                                <option value="Storage">Storage</option>
                                <option value="ColdStorage">Cold Storage</option>
                                <option value="Hazardous">Hazardous</option>
                                <option value="Receiving">Receiving</option>
                                <option value="Dispatch">Dispatch</option>
                                <option value="Inspection">Inspection</option>
                            </SelectBox>

                            <NumberInput
                                label="Capacity"
                                value={zoneForm.capacity}
                                onChange={(event) =>
                                    setZoneForm((prev) => ({
                                        ...prev,
                                        capacity: event.target.value,
                                    }))
                                }
                                min="0"
                                step="0.001"
                                required
                            />

                            <TextInput
                                label="Description"
                                value={zoneForm.description}
                                onChange={(event) =>
                                    setZoneForm((prev) => ({
                                        ...prev,
                                        description: event.target.value,
                                    }))
                                }
                                maxLength={300}
                            />

                            <label className="flex items-center gap-3 rounded-xl border border-slate-300 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={
                                        zoneForm.requiresTemperatureControl
                                    }
                                    onChange={(event) =>
                                        setZoneForm((prev) => ({
                                            ...prev,
                                            requiresTemperatureControl:
                                                event.target.checked,
                                            minTemperatureCelsius:
                                                event.target.checked
                                                    ? prev.minTemperatureCelsius
                                                    : "",
                                            maxTemperatureCelsius:
                                                event.target.checked
                                                    ? prev.maxTemperatureCelsius
                                                    : "",
                                        }))
                                    }
                                />
                                <span className="font-semibold text-slate-700">
                                    Temperature control
                                </span>
                            </label>

                            {zoneForm.requiresTemperatureControl && (
                                <>
                                    <NumberInput
                                        label="Minimum Temperature �C"
                                        value={
                                            zoneForm.minTemperatureCelsius
                                        }
                                        onChange={(event) =>
                                            setZoneForm((prev) => ({
                                                ...prev,
                                                minTemperatureCelsius:
                                                    event.target.value,
                                            }))
                                        }
                                        step="0.1"
                                        required
                                    />

                                    <NumberInput
                                        label="Maximum Temperature �C"
                                        value={
                                            zoneForm.maxTemperatureCelsius
                                        }
                                        onChange={(event) =>
                                            setZoneForm((prev) => ({
                                                ...prev,
                                                maxTemperatureCelsius:
                                                    event.target.value,
                                            }))
                                        }
                                        step="0.1"
                                        required
                                    />
                                </>
                            )}

                            <div className="md:col-span-2">
                                <SuccessButton
                                    type="submit"
                                    disabled={
                                        saving.zone ||
                                        !selectedWarehouseId
                                    }
                                >
                                    {saving.zone
                                        ? "Creating..."
                                        : "Create Zone"}
                                </SuccessButton>
                            </div>
                        </form>
                    </Panel>

                    <Panel
                        title="Warehouse Zones"
                        subtitle={
                            selectedWarehouse
                                ? `Zones in ${selectedWarehouse.name}.`
                                : "Select a warehouse."
                        }
                    >
                        {loading.zones ? (
                            <p className="text-slate-500">
                                Loading zones...
                            </p>
                        ) : (
                            <DataTable
                                columns={zoneColumns}
                                rows={zones}
                                rowKey="id"
                                emptyText="No warehouse zones found."
                            />
                        )}
                    </Panel>
                </div>
            )}

            {activeTab === "shelves" && (
                <div className="space-y-6">
                    <WarehouseSelector
                        warehouses={warehouses}
                        selectedWarehouseId={selectedWarehouseId}
                        setSelectedWarehouseId={setSelectedWarehouseId}
                    />

                    <ZoneSelector
                        zones={zones}
                        selectedZoneId={selectedZoneId}
                        setSelectedZoneId={setSelectedZoneId}
                    />

                    <Panel
                        title="Create Warehouse Shelf"
                        subtitle="Create a shelf inside the selected warehouse zone."
                    >
                        <form
                            onSubmit={handleCreateShelf}
                            className="grid gap-4 md:grid-cols-2"
                        >
                            <TextInput
                                label="Shelf Code"
                                value={shelfForm.code}
                                onChange={(event) =>
                                    setShelfForm((prev) => ({
                                        ...prev,
                                        code: event.target.value,
                                    }))
                                }
                                maxLength={50}
                                required
                            />

                            <TextInput
                                label="Notes"
                                value={shelfForm.notes}
                                onChange={(event) =>
                                    setShelfForm((prev) => ({
                                        ...prev,
                                        notes: event.target.value,
                                    }))
                                }
                                maxLength={300}
                            />

                            <NumberInput
                                label="Maximum Weight kg"
                                value={shelfForm.maxWeightKg}
                                onChange={(event) =>
                                    setShelfForm((prev) => ({
                                        ...prev,
                                        maxWeightKg: event.target.value,
                                    }))
                                }
                                min="0"
                                step="0.001"
                                required
                            />

                            <NumberInput
                                label="Maximum Volume m?"
                                value={shelfForm.maxVolumeM3}
                                onChange={(event) =>
                                    setShelfForm((prev) => ({
                                        ...prev,
                                        maxVolumeM3: event.target.value,
                                    }))
                                }
                                min="0"
                                step="0.0001"
                                required
                            />

                            <label className="flex items-center gap-3 rounded-xl border border-slate-300 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={shelfForm.isBlocked}
                                    onChange={(event) =>
                                        setShelfForm((prev) => ({
                                            ...prev,
                                            isBlocked:
                                                event.target.checked,
                                        }))
                                    }
                                />
                                <span className="font-semibold text-slate-700">
                                    Create as blocked
                                </span>
                            </label>

                            <div className="md:col-span-2">
                                <SuccessButton
                                    type="submit"
                                    disabled={
                                        saving.shelf ||
                                        !selectedZoneId
                                    }
                                >
                                    {saving.shelf
                                        ? "Creating..."
                                        : "Create Shelf"}
                                </SuccessButton>
                            </div>
                        </form>
                    </Panel>

                    <Panel
                        title="Warehouse Shelves"
                        subtitle={
                            selectedZone
                                ? `Shelves in ${selectedZone.name}.`
                                : "Select a warehouse zone."
                        }
                    >
                        {loading.shelves ? (
                            <p className="text-slate-500">
                                Loading shelves...
                            </p>
                        ) : (
                            <DataTable
                                columns={shelfColumns}
                                rows={shelves}
                                rowKey="id"
                                emptyText="No warehouse shelves found."
                            />
                        )}
                    </Panel>
                </div>
            )}

            {activeTab === "inventory" && (
                <div className="space-y-6">
                    <WarehouseSelector
                        warehouses={warehouses}
                        selectedWarehouseId={selectedWarehouseId}
                        setSelectedWarehouseId={setSelectedWarehouseId}
                    />

                    <ZoneSelector
                        zones={zones}
                        selectedZoneId={selectedZoneId}
                        setSelectedZoneId={setSelectedZoneId}
                    />

                    <div className="grid gap-6 xl:grid-cols-2">
                        <Panel
                            title="Store Inventory"
                            subtitle="Place a shipment or parcel on a warehouse shelf."
                        >
                            <form
                                onSubmit={handleStoreInventory}
                                className="space-y-4"
                            >
                                <SelectBox
                                    label="Shelf"
                                    value={
                                        inventoryForm.warehouseShelfId
                                    }
                                    onChange={(event) =>
                                        setInventoryForm((prev) => ({
                                            ...prev,
                                            warehouseShelfId:
                                                event.target.value,
                                        }))
                                    }
                                    required
                                >
                                    <option value="">
                                        Select shelf
                                    </option>

                                    {shelves
                                        .filter((item) => !item.isBlocked)
                                        .map((item) => (
                                            <option
                                                key={item.id}
                                                value={item.id}
                                            >
                                                {item.code}
                                            </option>
                                        ))}
                                </SelectBox>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <NumberInput
                                        label="Shipment ID"
                                        value={inventoryForm.shipmentId}
                                        onChange={(event) =>
                                            setInventoryForm((prev) => ({
                                                ...prev,
                                                shipmentId:
                                                    event.target.value,
                                            }))
                                        }
                                        min="1"
                                        step="1"
                                    />

                                    <NumberInput
                                        label="Parcel ID"
                                        value={inventoryForm.parcelId}
                                        onChange={(event) =>
                                            setInventoryForm((prev) => ({
                                                ...prev,
                                                parcelId:
                                                    event.target.value,
                                            }))
                                        }
                                        min="1"
                                        step="1"
                                    />
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <NumberInput
                                        label="Weight kg"
                                        value={inventoryForm.weightKg}
                                        onChange={(event) =>
                                            setInventoryForm((prev) => ({
                                                ...prev,
                                                weightKg:
                                                    event.target.value,
                                            }))
                                        }
                                        min="0.001"
                                        step="0.001"
                                        required
                                    />

                                    <NumberInput
                                        label="Volume m?"
                                        value={inventoryForm.volumeM3}
                                        onChange={(event) =>
                                            setInventoryForm((prev) => ({
                                                ...prev,
                                                volumeM3:
                                                    event.target.value,
                                            }))
                                        }
                                        min="0.0001"
                                        step="0.0001"
                                        required
                                    />
                                </div>

                                <TextInput
                                    label="Notes"
                                    value={inventoryForm.notes}
                                    onChange={(event) =>
                                        setInventoryForm((prev) => ({
                                            ...prev,
                                            notes: event.target.value,
                                        }))
                                    }
                                    maxLength={300}
                                />

                                <SuccessButton
                                    type="submit"
                                    disabled={saving.inventory}
                                >
                                    {saving.inventory
                                        ? "Storing..."
                                        : "Store Inventory"}
                                </SuccessButton>
                            </form>
                        </Panel>

                        <Panel
                            title="Move Inventory"
                            subtitle="Transfer an active inventory item to another shelf."
                        >
                            <form
                                onSubmit={handleMoveInventory}
                                className="space-y-4"
                            >
                                <SelectBox
                                    label="Inventory Item"
                                    value={moveForm.inventoryId}
                                    onChange={(event) =>
                                        setMoveForm((prev) => ({
                                            ...prev,
                                            inventoryId:
                                                event.target.value,
                                        }))
                                    }
                                    required
                                >
                                    <option value="">
                                        Select inventory item
                                    </option>

                                    {inventory
                                        .filter((item) => !item.releasedAt)
                                        .map((item) => (
                                            <option
                                                key={item.id}
                                                value={item.id}
                                            >
                                                Inventory #{item.id}
                                                {item.parcelId
                                                    ? ` � Parcel ${item.parcelId}`
                                                    : ""}
                                                {item.shipmentId
                                                    ? ` � Shipment ${item.shipmentId}`
                                                    : ""}
                                            </option>
                                        ))}
                                </SelectBox>

                                <SelectBox
                                    label="Target Shelf"
                                    value={moveForm.targetShelfId}
                                    onChange={(event) =>
                                        setMoveForm((prev) => ({
                                            ...prev,
                                            targetShelfId:
                                                event.target.value,
                                        }))
                                    }
                                    required
                                >
                                    <option value="">
                                        Select target shelf
                                    </option>

                                    {shelves
                                        .filter((item) => !item.isBlocked)
                                        .map((item) => (
                                            <option
                                                key={item.id}
                                                value={item.id}
                                            >
                                                {item.code}
                                            </option>
                                        ))}
                                </SelectBox>

                                <TextInput
                                    label="Notes"
                                    value={moveForm.notes}
                                    onChange={(event) =>
                                        setMoveForm((prev) => ({
                                            ...prev,
                                            notes: event.target.value,
                                        }))
                                    }
                                    maxLength={300}
                                />

                                <PrimaryButton
                                    type="submit"
                                    disabled={saving.move}
                                >
                                    {saving.move
                                        ? "Moving..."
                                        : "Move Inventory"}
                                </PrimaryButton>
                            </form>
                        </Panel>
                    </div>

                    <Panel
                        title="Warehouse Inventory"
                        subtitle={
                            selectedWarehouse
                                ? `Inventory in ${selectedWarehouse.name}.`
                                : "Select a warehouse."
                        }
                    >
                        <label className="mb-4 flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={includeReleased}
                                onChange={(event) =>
                                    setIncludeReleased(
                                        event.target.checked
                                    )
                                }
                            />
                            <span className="font-semibold text-slate-700">
                                Include released inventory
                            </span>
                        </label>

                        {loading.inventory ? (
                            <p className="text-slate-500">
                                Loading inventory...
                            </p>
                        ) : (
                            <DataTable
                                columns={inventoryColumns}
                                rows={inventory}
                                rowKey="id"
                                emptyText="No warehouse inventory found."
                            />
                        )}
                    </Panel>
                </div>
            )}

            {activeTab === "operations" && (
                <div className="space-y-6">
                    <WarehouseSelector
                        warehouses={warehouses}
                        selectedWarehouseId={selectedWarehouseId}
                        setSelectedWarehouseId={setSelectedWarehouseId}
                    />

                    <Panel
                        title="Add Warehouse Operation"
                        subtitle="Record a shipment or parcel operation."
                    >
                        <form
                            onSubmit={handleAddOperation}
                            className="grid gap-4 md:grid-cols-2"
                        >
                            <NumberInput
                                label="Shipment ID"
                                value={operationForm.shipmentId}
                                onChange={(event) =>
                                    setOperationForm((prev) => ({
                                        ...prev,
                                        shipmentId:
                                            event.target.value,
                                    }))
                                }
                                min="1"
                                step="1"
                            />

                            <NumberInput
                                label="Parcel ID"
                                value={operationForm.parcelId}
                                onChange={(event) =>
                                    setOperationForm((prev) => ({
                                        ...prev,
                                        parcelId:
                                            event.target.value,
                                    }))
                                }
                                min="1"
                                step="1"
                            />

                            <SelectBox
                                label="Operation Type"
                                value={operationForm.operationType}
                                onChange={(event) =>
                                    setOperationForm((prev) => ({
                                        ...prev,
                                        operationType:
                                            event.target.value,
                                    }))
                                }
                            >
                                <option value="Received">Received</option>
                                <option value="Stored">Stored</option>
                                <option value="Released">Released</option>
                                <option value="Transferred">Transferred</option>
                                <option value="Returned">Returned</option>
                                <option value="Inspected">Inspected</option>
                            </SelectBox>

                            <TextInput
                                label="Notes"
                                value={operationForm.notes}
                                onChange={(event) =>
                                    setOperationForm((prev) => ({
                                        ...prev,
                                        notes: event.target.value,
                                    }))
                                }
                                maxLength={500}
                            />

                            <div className="md:col-span-2">
                                <PrimaryButton
                                    type="submit"
                                    disabled={saving.operation}
                                >
                                    {saving.operation
                                        ? "Adding..."
                                        : "Add Operation"}
                                </PrimaryButton>
                            </div>
                        </form>
                    </Panel>

                    <Panel
                        title="Warehouse Operations"
                        subtitle={
                            selectedWarehouse
                                ? `Operations for ${selectedWarehouse.name}.`
                                : "Select a warehouse."
                        }
                    >
                        {loading.operations ? (
                            <p className="text-slate-500">
                                Loading warehouse operations...
                            </p>
                        ) : (
                            <DataTable
                                columns={operationColumns}
                                rows={operations}
                                rowKey="id"
                                emptyText="No warehouse operations found."
                            />
                        )}
                    </Panel>
                </div>
            )}
        </div>
    );
}

function WarehouseSelector({
    warehouses,
    selectedWarehouseId,
    setSelectedWarehouseId,
}) {
    return (
        <Panel
            title="Warehouse Selection"
            subtitle="Select the active warehouse."
        >
            <SelectBox
                label="Warehouse"
                value={selectedWarehouseId}
                onChange={(event) =>
                    setSelectedWarehouseId(event.target.value)
                }
            >
                <option value="">Select warehouse</option>

                {warehouses.map((item) => (
                    <option key={item.id} value={item.id}>
                        {item.name || `Warehouse #${item.id}`}
                    </option>
                ))}
            </SelectBox>
        </Panel>
    );
}

function ZoneSelector({
    zones,
    selectedZoneId,
    setSelectedZoneId,
}) {
    return (
        <Panel
            title="Zone Selection"
            subtitle="Select the active warehouse zone."
        >
            <SelectBox
                label="Warehouse Zone"
                value={selectedZoneId}
                onChange={(event) =>
                    setSelectedZoneId(event.target.value)
                }
            >
                <option value="">Select warehouse zone</option>

                {zones.map((item) => (
                    <option key={item.id} value={item.id}>
                        {item.name || `Zone #${item.id}`}
                    </option>
                ))}
            </SelectBox>
        </Panel>
    );
}

function formatNumber(value) {
    const number = Number(value);

    return Number.isFinite(number)
        ? number.toLocaleString(undefined, {
            maximumFractionDigits: 4,
        })
        : "�";
}

function formatDateTime(value) {
    if (!value) {
        return "�";
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? "�"
        : date.toLocaleString();
}